import {
  createGameAccountSchema,
  setAccountVaultPasswordSchema,
  updateGameAccountSchema,
  type CreateGameAccountInput,
  type GameAccountSecretField,
  type SetAccountVaultPasswordPayload,
  type UpdateGameAccountInput,
} from '@gc/shared';

import { ApiError } from '@/lib/api';
import type { FieldError } from './useSettings';

/**
 * The Game Accounts screen's decisions, extracted so they are testable in
 * plain node (this workspace ships no DOM emulation, so a button can never be
 * clicked in a test — the gate and the builders ARE the seam, exactly as
 * `gameLogic.ts` is for the Games drawer).
 *
 * Three things live here (owner, 2026-09-08):
 *
 * - WHO may touch an account. An account now carries its login mailbox's
 *   password beside the launcher password, and the PCs' Accounts app shows
 *   every one of them to whoever knows the vault password — so adding,
 *   editing, deleting and revealing are the OWNER's alone. The API refuses
 *   everyone else with `owner_only`; a screen must not offer a control that
 *   can only fail.
 * - The drawer's wire payload, with the mailbox password's THREE states on
 *   edit — untouched, replaced, cleared. The two-state
 *   `...(value ? { value } : {})` shape cannot say "clear it"; that exact
 *   collapse shipped the cover-image bug `gameLogic.ts` documents.
 * - The vault password form, checked against the SAME schema the API applies
 *   so a refused password never costs a round-trip and the rule shown is the
 *   contract's own.
 */

/* -------------------------------------------------------------------------- */
/* Who may do what                                                            */
/* -------------------------------------------------------------------------- */

export interface GameAccountControls {
  /** Add, Edit, Delete — and the drawer they open. */
  canManage: boolean;
  /** The per-secret reveal buttons. */
  canReveal: boolean;
  /** The vault password form. The status line itself shows to everyone. */
  canSetVaultPassword: boolean;
  /** The quiet "only the owner can change accounts" note. */
  showsOwnerOnlyNote: boolean;
}

/**
 * Everything or nothing, deliberately: there is no half-way role here. Staff
 * who hold `gameAccounts.manage` still SEE the list — it carries no secret,
 * and knowing which account is on which PC is their job — but every control
 * that would hit an owner-only route stays off the screen.
 */
export function gameAccountControlsFor(
  employee: { isOwner: boolean } | null | undefined,
): GameAccountControls {
  const isOwner = employee?.isOwner === true;
  return {
    canManage: isOwner,
    canReveal: isOwner,
    canSetVaultPassword: isOwner,
    showsOwnerOnlyNote: !isOwner,
  };
}

/** The API's refusal when a non-owner reaches an owner-only route anyway. */
export function isOwnerOnlyRefusal(caught: unknown): boolean {
  return caught instanceof ApiError && caught.code === 'owner_only';
}

/* -------------------------------------------------------------------------- */
/* The drawer's payload                                                       */
/* -------------------------------------------------------------------------- */

/** The drawer's fields as it holds them — strings, blank meaning "none". */
export interface GameAccountDraft {
  accountName: string;
  username: string;
  /** Blank on edit means "keep the stored one". Required on create. */
  password: string;
  company: string;
  /**
   * The games this account owns. No per-game device allowances any more
   * (owner, 2026-10-05): only the lease read them, and an absent key leaves
   * the stored rows alone.
   */
  gameIds: string[];
  /** Trimmed; blank means none. */
  email: string;
  /** Blank means none on create and "keep the stored one" on edit. */
  emailPassword: string;
  /**
   * Edit only: the "clear the stored mailbox password" box. It WINS over a
   * typed value — the box is the explicit instruction, and the drawer greys
   * the input out while it is ticked so the two cannot disagree on screen.
   */
  clearEmailPassword: boolean;
  /** Trimmed; blank means none. */
  notes: string;
  /**
   * "Made for PC" (owner, 2026-09-23): a device id, or blank for a
   * venue-wide account. Absent when the form has no such field, and then
   * nothing is sent about it.
   */
  homeDeviceId?: string;
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

/** Build the `POST /settings/game-accounts` body. */
export function buildCreateGameAccountPayload(draft: GameAccountDraft): CreateGameAccountInput {
  const payload: CreateGameAccountInput = {
    accountName: draft.accountName,
    username: draft.username,
    password: draft.password,
    company: draft.company,
    gameIds: draft.gameIds,
    email: blankToNull(draft.email),
    notes: blankToNull(draft.notes),
  };
  if (draft.homeDeviceId !== undefined) payload.homeDeviceId = blankToNull(draft.homeDeviceId);
  // Omitted when blank: the schema's `min(1)` would refuse an empty string,
  // and "no mailbox password" is an absent key, not an empty one.
  if (draft.emailPassword) payload.emailPassword = draft.emailPassword;
  return payload;
}

/**
 * Build the `PATCH /settings/game-accounts/:id` body.
 *
 * Both secrets are OMITTED when blank so the stored ciphertext is left alone;
 * the mailbox password alone can also be posted as `null`, which is what
 * actually clears the column. `email` and `notes` are always sent — a field
 * the owner blanked out is an instruction, and the schemas take null.
 */
export function buildUpdateGameAccountPayload(draft: GameAccountDraft): UpdateGameAccountInput {
  const payload: UpdateGameAccountInput = {
    accountName: draft.accountName,
    username: draft.username,
    company: draft.company,
    gameIds: draft.gameIds,
    email: blankToNull(draft.email),
    notes: blankToNull(draft.notes),
  };
  // Sent whenever the form has the field, like email and notes: choosing
  // "venue-wide" is an instruction, and null is how the schema says it.
  if (draft.homeDeviceId !== undefined) payload.homeDeviceId = blankToNull(draft.homeDeviceId);
  if (draft.password) payload.password = draft.password;
  if (draft.clearEmailPassword) payload.emailPassword = null;
  else if (draft.emailPassword) payload.emailPassword = draft.emailPassword;
  return payload;
}

/**
 * Runs the body through the SAME zod schema the API applies and hands back
 * the per-field list the drawer already renders — so a malformed email lands
 * on its own field, in the contract's wording, before any request is made.
 *
 * Only the first path segment is kept: the drawer has a field per top-level
 * key and nothing finer, and an issue it cannot place still surfaces at the
 * root rather than vanishing.
 */
export function validateGameAccountPayload(
  payload: CreateGameAccountInput | UpdateGameAccountInput,
  mode: 'create' | 'edit',
): FieldError[] {
  const parsed =
    mode === 'create'
      ? createGameAccountSchema.safeParse(payload)
      : updateGameAccountSchema.safeParse(payload);
  if (parsed.success) return [];
  return parsed.error.issues.map((issue) => ({
    field: issue.path.length > 0 ? String(issue.path[0]) : '(root)',
    message: issue.message,
  }));
}

/* -------------------------------------------------------------------------- */
/* Reveals                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * One key per (account, secret): the two secrets of one account are revealed
 * and re-masked independently, on their own 30-second clocks.
 */
export function revealKey(accountId: string, field: GameAccountSecretField): string {
  return `${accountId}:${field}`;
}

/* -------------------------------------------------------------------------- */
/* The vault password form                                                    */
/* -------------------------------------------------------------------------- */

/**
 * What is wrong with the typed vault password, as an i18n key, not a sentence.
 * A list rather than a bare union so the strings test can prove every one of
 * them has a caption in every language: the card builds
 * `settings.gameAccounts.vault.<problem>` at run time, which no grep of the
 * component can see.
 */
export const VAULT_PASSWORD_PROBLEMS = ['tooShort', 'tooLong', 'needsLettersAndNumbers'] as const;
export type VaultPasswordProblem = (typeof VAULT_PASSWORD_PROBLEMS)[number];

export interface VaultPasswordCheck {
  /** True only when the schema passes AND the confirmation matches. */
  ok: boolean;
  passwordProblem: VaultPasswordProblem | null;
  /** The confirmation differs from the password (an empty confirmation counts). */
  mismatch: boolean;
  /** The body to PUT, present only when `ok`. */
  payload: SetAccountVaultPasswordPayload | null;
}

/**
 * The schema is the authority — this only names its first complaint so the
 * card can say it in the staff member's language. Zod runs every check, and
 * they sit in the order written (min, max, then the letters-and-numbers
 * refinement), so a short password is called short before it is called
 * letter-less.
 */
export function checkVaultPasswordForm(password: string, confirm: string): VaultPasswordCheck {
  const parsed = setAccountVaultPasswordSchema.safeParse({ password });
  const passwordProblem = parsed.success ? null : problemOf(parsed.error.issues[0]);
  const mismatch = password !== confirm;
  const ok = parsed.success && !mismatch;
  return {
    ok,
    passwordProblem,
    mismatch,
    payload: ok && parsed.success ? parsed.data : null,
  };
}

function problemOf(issue: { code: string } | undefined): VaultPasswordProblem {
  switch (issue?.code) {
    case 'too_small':
      return 'tooShort';
    case 'too_big':
      return 'tooLong';
    default:
      return 'needsLettersAndNumbers';
  }
}
